// Pure rules behind the Feature Access page, kept out of the component so they
// are directly testable.

export interface OrgLike {
  id: string;
  name: string;
  type?: string | null;
  parentOrganizationId?: string | null;
}

export interface GrantLike {
  organizationId: string;
  feature: string;
}

export interface ProviderRow {
  id: string;
  name: string;
  /** 'SERVICE' (provider) or 'BUSINESS'. */
  kind: 'SERVICE' | 'BUSINESS';
  /** The platform root holds every feature implicitly and cannot be toggled. */
  isRoot: boolean;
  granted: boolean;
}

/**
 * One row per PROVIDER (SERVICE) organisation — and, for a feature a business may
 * hold (`includeBusinesses`: LIVE_CALL_SPY), one per BUSINESS after them. For every
 * other feature businesses are left out on purpose: they are platform configuration
 * a business may not manage, and the server refuses a grant on one.
 *
 * The root — the SERVICE org with no parent, Green Giraffe — is listed first,
 * as implicitly granted, so the page answers "who can do this?" completely.
 */
export function providerRows(orgs: OrgLike[], grants: GrantLike[], feature: string,
                             includeBusinesses = false): ProviderRow[] {
  const granted = new Set(grants.filter(g => g.feature === feature).map(g => g.organizationId));
  const seen = new Set<string>();
  const rows: ProviderRow[] = [];
  for (const o of orgs) {
    if (!o?.id || seen.has(o.id)) continue;
    const type = (o.type || '').toUpperCase();
    if (type !== 'SERVICE' && !(includeBusinesses && type === 'BUSINESS')) continue;
    seen.add(o.id);
    const kind = type === 'SERVICE' ? 'SERVICE' : 'BUSINESS';
    const isRoot = kind === 'SERVICE' && !o.parentOrganizationId;
    rows.push({ id: o.id, name: o.name || o.id, kind, isRoot, granted: isRoot || granted.has(o.id) });
  }
  const rank = (r: ProviderRow) => (r.isRoot ? 0 : r.kind === 'SERVICE' ? 1 : 2);
  return rows.sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
}
